import { useEffect, useRef, ReactNode, FC } from 'react';
import { X } from 'lucide-react';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  closeOnBackdrop?: boolean;
  bodyClassName?: string;
}

const widthClasses = {
  sm: 'max-w-xs',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

export const Drawer: FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'md',
  closeOnBackdrop = true,
  bodyClassName,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="backdrop-blur-xs fixed inset-0 bg-black/50 transition-opacity duration-200"
        onClick={() => closeOnBackdrop && onClose()}
        aria-hidden="true"
      />

      {/* Slide-over panel container */}
      <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
        <div
          ref={drawerRef}
          className={`w-screen ${widthClasses[width]} pointer-events-auto flex transform flex-col justify-between border-l border-surface-border bg-surface-panel text-content-main shadow-2xl transition-transform duration-200 ease-in-out`}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-surface-border px-5 py-4">
            <div className="min-w-0 pr-3">
              {title && (
                <h3 className="truncate text-sm font-semibold text-content-main">
                  {title}
                </h3>
              )}
              {description && (
                <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
                  {description}
                </p>
              )}
            </div>

            <button
              onClick={onClose}
              aria-label="Fechar painel"
              className="flex-shrink-0 cursor-pointer rounded-lg p-1.5 text-content-muted transition-colors hover:bg-surface-subtle hover:text-content-main"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div
            className={`flex-1 overflow-y-auto text-xs text-content-main ${
              bodyClassName !== undefined ? bodyClassName : 'space-y-4 p-5'
            }`}
          >
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-surface-border bg-surface-subtle px-5 py-3.5">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
